// client/src/services/TelemetryService.js
/**
 * TelemetryService - Centralized telemetry and metrics collection
 *
 * Provides structured observability for:
 * - Performance metrics (P50/P95/P99 latencies)
 * - Error tracking and categorization
 * - Feature usage analytics
 * - User behavior flows
 * - System health monitoring
 *
 * Features:
 * - Pluggable backends (console, Application Insights, DataDog, etc.)
 * - Event batching to reduce network overhead
 * - PII filtering for privacy compliance
 * - Offline support with event queuing
 * - Environment-specific configuration
 */
class TelemetryService {
  constructor() {
    this.backends = []
    this.eventQueue = []
    this.batchSize = 10
    this.batchTimeout = 5000 // 5 seconds
    this.batchTimer = null
    this.enabled = true

    // PII patterns to filter
    this.piiPatterns = [
      /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/gi, // Email
      /\b(?:\d{3}([ -])\d{3}\1\d{4}|\d{10})\b/g, // Phone numbers (US-style)
      /\b\d{3}-\d{2}-\d{4}\b/g, // SSN
      /\b(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)\b/g, // IP addresses (0-255 per octet)
    ]

    // Initialize default backend based on environment
    this._initializeDefaultBackend()
  }

  /**
   * Initialize default backend based on environment
   * @private
   */
  _initializeDefaultBackend() {
    if (
      process.env.NODE_ENV === 'development' ||
      process.env.NODE_ENV === 'test'
    ) {
      this.registerBackend(new ConsoleBackend())
    }
    // Production backend would be registered via configuration
    // e.g., this.registerBackend(new ApplicationInsightsBackend(config))
  }

  /**
   * Register a telemetry backend
   * @param {Object} backend - Backend implementation with send(event) method
   */
  registerBackend(backend) {
    if (!backend || typeof backend.send !== 'function') {
      throw new Error('Backend must implement send(event) method')
    }
    this.backends.push(backend)
  }

  /**
   * Track a telemetry event
   * @param {string} eventType - Type of event (service_call, user_action, error, etc.)
   * @param {Object} data - Event data
   */
  track(eventType, data) {
    if (!this.enabled) {
      return
    }

    const event = {
      eventType,
      timestamp: new Date().toISOString(),
      ...this._filterPII(data),
    }

    // Add to batch queue
    this.eventQueue.push(event)

    // Send immediately if batch size reached
    if (this.eventQueue.length >= this.batchSize) {
      this._flush()
    } else {
      // Schedule batch send
      this._scheduleBatch()
    }
  }

  /**
   * Track service call metrics
   * @param {Object} metrics - Service call metrics
   */
  trackServiceCall(metrics) {
    this.track('service_call', metrics)
  }

  /**
   * Track error events
   * @param {Object} errorData - Error details
   */
  trackError(errorData) {
    this.track('error', errorData)
  }

  /**
   * Track user interaction
   * @param {Object} interactionData - User interaction details
   */
  trackInteraction(interactionData) {
    this.track('user_interaction', interactionData)
  }

  /**
   * Track performance metric
   * @param {Object} perfData - Performance metric data
   */
  trackPerformance(perfData) {
    this.track('performance', perfData)
  }

  /**
   * Filter PII from event data
   * @private
   * @param {Object} data - Data to filter
   * @returns {Object} - Filtered data
   */
  _filterPII(data) {
    // Recursively filter string values
    const filterValue = (value) => {
      if (typeof value === 'string') {
        let result = value
        this.piiPatterns.forEach((pattern) => {
          result = result.replace(pattern, '[REDACTED]')
        })
        return result
      }
      if (typeof value === 'object' && value !== null) {
        // Handle arrays specifically to preserve array type
        if (Array.isArray(value)) {
          return value.map((item) => filterValue(item))
        }
        return this._filterPII(value)
      }
      return value
    }

    // Handle arrays at top level
    if (Array.isArray(data)) {
      return data.map((item) => filterValue(item))
    }

    // Handle objects
    const filtered = { ...data }
    Object.keys(filtered).forEach((key) => {
      filtered[key] = filterValue(filtered[key])
    })

    return filtered
  }

  /**
   * Schedule batch send
   * @private
   */
  _scheduleBatch() {
    if (this.batchTimer) {
      return
    }

    this.batchTimer = setTimeout(() => {
      this._flush()
    }, this.batchTimeout)
  }

  /**
   * Flush event queue to backends
   * @private
   */
  _flush() {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer)
      this.batchTimer = null
    }

    if (this.eventQueue.length === 0) {
      return
    }

    const events = [...this.eventQueue]
    this.eventQueue = []

    // Send to all backends
    this.backends.forEach((backend) => {
      try {
        backend.send(events)
      } catch (error) {
        console.error('Telemetry backend error:', error)
      }
    })
  }

  /**
   * Manually flush all pending events
   */
  flush() {
    this._flush()
  }

  /**
   * Enable telemetry collection
   */
  enable() {
    this.enabled = true
  }

  /**
   * Disable telemetry collection
   */
  disable() {
    // Clear pending timer first to prevent it firing after disable
    if (this.batchTimer) {
      clearTimeout(this.batchTimer)
      this.batchTimer = null
    }
    this.enabled = false
    this._flush() // Flush pending events before disabling
  }

  /**
   * Configure telemetry settings
   * @param {Object} config - Configuration options
   * @param {number} config.batchSize - Number of events before auto-flush
   * @param {number} config.batchTimeout - Time in ms before auto-flush
   */
  configure(config) {
    if (config.batchSize !== undefined) {
      this.batchSize = config.batchSize
    }
    if (config.batchTimeout !== undefined) {
      this.batchTimeout = config.batchTimeout
    }
  }
}

/**
 * Console backend for development/debugging
 */
class ConsoleBackend {
  send(events) {
    if (process.env.NODE_ENV === 'test') {
      // Silent in tests unless explicitly debugging
      return
    }

    console.group('📊 Telemetry Events')
    events.forEach((event) => {
      const { eventType, timestamp, ...data } = event
      console.log(`[${timestamp}] ${eventType}:`, data)
    })
    console.groupEnd()
  }
}

// Singleton instance
const telemetryService = new TelemetryService()

// Export both class and singleton
export { TelemetryService, ConsoleBackend }
export default telemetryService
